import type { Path } from '~/core/router'
import type { Navigate, NavigationParameters } from '../index.h'

import { createWatch } from 'effector'
import { model as router } from '~/core/router'
import { mq } from '~/shared/mq'
import { NavigateEvent } from '../events/navigate'
import { logger } from '../index.h'
import { getSearchSubstring } from '../lib'

/**
 * Navigation state snapshot captured at emit time.
 */
export type NavigationSnapshot = {
  timestamp: number
  from: string
  to: string
  queryParams: Record<string, string | string[]>
}

/**
 * NavigationWatcher monitors app models do redirects.
 * Emits navigation Events into bus.
 */
export class NavigationWatcher {
  private unsubscribe: (() => void)[] = []
  private lastRoute: string = ''
  private lastSearch: string = ''

  constructor() {
    this.emitNavigationEvent({
      to: window.location.pathname,
      from: '',
      type: 'initial', // from is undefined in this case
      search: window.location.search,
    })
    this.lastRoute = window.location.pathname
    this.lastSearch = window.location.search

    this.startWatchRoutingModel()

    // TODO Later "startWatchRoutingModel and listent to 'popstate'" could be swapped with Navigation API.
    // This api already supported in all browsers, but only one recent Safari version supports it.
    // https://developer.mozilla.org/en-US/docs/Web/API/Navigation_API
    window.addEventListener('popstate', this.handleBrowserNavigation.bind(this))
  }

  private startWatchRoutingModel() {
    this.unsubscribe = [
      createWatch({
        unit: router.navigatePush,
        fn: this.handleModelNavigation('push'),
      }),
      createWatch({
        unit: router.navigateReplace,
        fn: this.handleModelNavigation('replace'),
      }),
      createWatch({
        unit: router.navigateGo,
        fn: this.handleModelNavigation('go'),
      }),
      createWatch({
        unit: router.navigateBack,
        fn: this.handleModelNavigation('back'),
      }),
      createWatch({
        unit: router.navigateForward,
        fn: this.handleModelNavigation('forward'),
      }),
      // There are group of events modifing search query string, no actual navigation but affecting query string.
      // We need to watch them and emit navigation event for analytics.
      createWatch({
        unit: router.$search,
        fn: this.handleQueryChange,
      }),
    ]
  }

  private handleQueryChange = (search: string | null) => {
    if (search == null) return

    const newSearch = search

    if (newSearch === this.lastSearch) return

    const to = this.lastRoute
    const from = this.lastRoute

    this.emitNavigationEvent({
      to,
      from,
      type: 'query',
      search: newSearch,
    })

    this.lastSearch = newSearch
  }

  // this handler is for browser back/forward buttons and other non-router navigations
  // possible param event: PopStateEvent
  private handleBrowserNavigation() {
    const from = this.lastRoute
    const to = window.location.pathname
    const search = window.location.search
    const type = ''

    // Emit navigation event for analytics
    this.emitNavigationEvent({ to, from, type, search })

    // Update state
    this.lastRoute = to
    this.lastSearch = search
  }

  /**
   * Handles router model events.
   */
  private handleModelNavigation(
    type: 'push' | 'replace' | 'go' | 'back' | 'forward'
  ) {
    return (payload: router.ToEx | number | string | void) => {
      let to = ''
      let search = ''
      const from = this.lastRoute

      // Handle different payload types
      if (typeof payload === 'object' && payload !== null) {
        // push, replace with object
        const p = payload as Partial<Path>
        to = p.pathname ?? ''
        search = getSearchSubstring(p.search, p.pathname)
      } else if (typeof payload === 'string') {
        // push, replace with string path
        to = payload
        search = getSearchSubstring(undefined, payload)
      } else if (typeof payload === 'number' || payload == null) {
        // not possible to get `to` from payload in that case
      }

      // Emit navigation event for analytics
      this.emitNavigationEvent({ to, from, type, search })

      // Update state
      this.lastRoute = to
      this.lastSearch = search
    }
  }

  /**
   * Emit navigation event for analytics.
   */
  private emitNavigationEvent({ to, from, type, search }: Navigate) {
    const timestamp = Date.now()

    this.trace({ to, from, type, search, timestamp })

    const event = new NavigateEvent({
      to,
      from,
      timestamp,
      search,
    })

    mq.emit(event)
  }

  /**
   * Dispose watchers and cleanup.
   */
  off() {
    this.unsubscribe.forEach((f) => f())
    this.unsubscribe = []
  }

  // ─── Debug ────────────────────────────────────────────────────────────────

  private trace({ from, to, type, search }: NavigationParameters) {
    logger.trace({ from, to, type, search })
  }
}
